// PageVideoBlockValue.jsx

import React, { useEffect } from 'react';

// Utilities
import {
  hasPageVideoMedia,
  isHostedPageAudience,
  isPageVideoHlsReady,
  isPageVideoPlayable,
  isPageVideoProcessing,
  logPageVideoDebug,
} from './pageVideoBlockUtils';

// CSS
import './PageVideoBlockValue.css';

// Components
import VideoView from 'nodetypes/video/view/VideoView';

function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * PageVideoBlockValue
 *
 * Renders a page VIDEO block with upload/processing/ready states.
 *
 * @returns {ReactNode} The rendered component.
 */
const PageVideoBlockValue = React.memo(({
  blockTitle = '',
  isUploading = false,
  node = null,
  pagePlaybackContext = null,
  uploadError = '',
}) => {

  // ----- CONTEXT ----- //

  const title = normalizeString(blockTitle);
  const isProcessing = isPageVideoProcessing(node, pagePlaybackContext);
  const isPlayable = isPageVideoPlayable(node, pagePlaybackContext);

  // ----- EFFECTS ----- //

  useEffect(() => {
    logPageVideoDebug('video-block-state', {
      blockTitle: title,
      hasMedia: hasPageVideoMedia(node),
      hlsReady: isPageVideoHlsReady(node),
      isPlayable,
      isProcessing,
      nodeKey: node?.key || '',
      audience: pagePlaybackContext?.audience || '',
      pinLength: normalizeString(pagePlaybackContext?.pin).length,
      publicPlayback: isHostedPageAudience(pagePlaybackContext),
      paths: {
        nodePath: normalizeString(pagePlaybackContext?.nodePath),
        pagePath: normalizeString(pagePlaybackContext?.pagePath),
        basePath: normalizeString(pagePlaybackContext?.basePath),
      },
    });
  }, [isPlayable, isProcessing, node, pagePlaybackContext, title]);

  // ----- RENDER ----- //

  return (
    <div className="page-video-block-value">

      {/* TITLE */}
      {title ? (
        <div
          className="page-video-block-value-title"
        >
          {title}
        </div>
      ) : null}

      {isUploading ? (
        <div
          className="page-video-block-value-state"
        >
          Uploading video...
        </div>
      ) : uploadError ? (
        <div
          className="page-video-block-value-state page-video-block-value-state-error"
        >
          {uploadError}
        </div>
      ) : !node ? (
        <div
          className="page-video-block-value-state"
        >
          Video not found.
        </div>
      ) : isProcessing ? (
        <div
          className="page-video-block-value-state"
        >
          {isHostedPageAudience(pagePlaybackContext)
            ? 'Preparing video for streaming. This usually takes a few minutes — the page will update automatically.'
            : 'Processing video...'}
        </div>
      ) : isPlayable ? (

        <div className="page-video-block-value-player">
          <VideoView
            autoplay={false}
            autosize={true}
            loop={false}
            node={node}
            pagePlaybackContext={pagePlaybackContext}
            showControls={true}
          />
        </div>
      ) : (
        <div
          className="page-video-block-value-state"
        >
          Video is not ready to play yet.
        </div>
      )}
    </div>
  );
});

// Dev Tools
PageVideoBlockValue.displayName = 'PageVideoBlockValue';

export default PageVideoBlockValue;
